import { forwardRef } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost" | "accent";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Size of the button */
  size?: ButtonSize;
  /** Show loading spinner and disable interactions */
  loading?: boolean;
  /** Content to display when loading */
  loadingText?: string;
  /** Button contents */
  children: ReactNode;
}

/** Shared utility for generating button CSS classes */
export function getButtonClasses(
  variant: ButtonVariant,
  size: ButtonSize,
  className?: string,
): string {
  const classes = ["btn", `btn-${variant}`];
  if (size !== "md") {
    classes.push(`btn-${size}`);
  }
  if (className) {
    classes.push(className);
  }
  return classes.join(" ");
}

/**
 * Primary button component for user actions.
 *
 * @example
 * ```tsx
 * <Button variant="primary" onClick={handleSubmit}>Save</Button>
 * <Button variant="danger" size="sm">Delete</Button>
 * <Button loading loadingText="Saving...">Save</Button>
 * ```
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      loading = false,
      loadingText,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        className={getButtonClasses(variant, size, className)}
        disabled={disabled || loading}
        {...props}
      >
        {loading ? (
          <>
            <span className="loading-spinner" aria-hidden="true" />
            {loadingText || children}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = "Button";
